import { GlobeIcon, ShuffleIcon } from "lucide-react";

import { useSkillSharePinnedVersionData } from "./useSkillSharePinnedVersionData";

import {
  PinnedVersionSelector,
  ShareSessionCreateViewLayout,
  ShareSessionSettingRow,
  type PinnedVersionSelection,
} from "@/components/SharedSessions";

interface CreateViewProps {
  workspaceId: number;
  collectionId: string;
  name: string;
  onNameChange: (value: string) => void;
  isPublic: boolean;
  onIsPublicChange: (value: boolean) => void;
  versionSelection: PinnedVersionSelection;
  onVersionSelectionChange: (value: PinnedVersionSelection) => void;
  hasExistingSessions: boolean;
  isCreateDisabled: boolean;
  isPending: boolean;
  remixEnabled: boolean;
  onRemixEnabledChange: (value: boolean) => void;
  onCreate: () => void;
  onBack: () => void;
}

export const CreateView = ({
  workspaceId,
  collectionId,
  name,
  onNameChange,
  isPublic,
  onIsPublicChange,
  versionSelection,
  onVersionSelectionChange,
  hasExistingSessions,
  isCreateDisabled,
  isPending,
  remixEnabled,
  onRemixEnabledChange,
  onCreate,
  onBack,
}: CreateViewProps) => {
  const { versions, labels } = useSkillSharePinnedVersionData(workspaceId, collectionId);

  return (
    <ShareSessionCreateViewLayout
      title="Share this skill collection"
      description="Create a read-only link to share this skill collection."
      namePlaceholder="Link name, e.g. Claude Code Demo"
      name={name}
      onNameChange={onNameChange}
      hasExistingSessions={hasExistingSessions}
      isCreateDisabled={isCreateDisabled}
      isPending={isPending}
      onCreate={onCreate}
      onBack={onBack}
      pendingButtonLabel="Creating..."
      settings={
        <>
          <PinnedVersionSelector
            value={versionSelection}
            onChange={onVersionSelectionChange}
            versions={versions}
            labels={labels}
            selectTriggerClassName="h-7 w-[180px]"
            latestDescription="Always use the latest saved version"
            labelDescription="Pinned to release label"
            versionDescription="Pinned to a specific version"
          />
          <ShareSessionSettingRow
            icon={GlobeIcon}
            label="Public access"
            description={
              isPublic
                ? "Visible to anyone with the link"
                : "Restricted to people in your organization"
            }
            checked={isPublic}
            onChange={onIsPublicChange}
          />
          <ShareSessionSettingRow
            icon={ShuffleIcon}
            label="Remixing"
            description="Visitors can copy this skill collection into their workspace"
            checked={remixEnabled}
            onChange={onRemixEnabledChange}
          />
        </>
      }
    />
  );
};
